import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { API } from "@/lib/data";

const inputCls =
  "w-full rounded-xl border border-emerald-500/25 bg-[#06130D]/70 px-4 py-2.5 text-sm text-amber-50 outline-none transition-colors focus:border-amber-400/70";
const labelCls = "mb-1.5 block font-mono-x text-[10px] uppercase tracking-[0.2em] text-emerald-100/50";

export const AdminSettingsTab = ({ token }) => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setSettings(data);
    } catch {
      toast.error("Could not load settings");
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  if (!settings) return <p className="py-16 text-center text-emerald-100/40">Loading settings…</p>;

  const setPkg = (id, field, value) =>
    setSettings((s) => ({ ...s, packages: s.packages.map((p) => (p.id === id ? { ...p, [field]: value } : p)) }));
  const setAddon = (field, value) => setSettings((s) => ({ ...s, addon: { ...s.addon, [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/settings`,
        {
          phone_display: settings.phone_display,
          addon: settings.addon,
          packages: settings.packages.map((p) => ({
            id: p.id, name: p.name, price: parseFloat(p.price), badge: p.badge,
            subtitle: p.subtitle, coverage: p.coverage, bestFor: p.bestFor, included: p.included,
          })),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Saved — the website is updated instantly");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings-tab">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-amber-50">Site Settings</h2>
          <p className="mt-1 text-xs text-emerald-100/50">Edit prices, names and package contents — changes go live on the website the moment you save.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="settings-save-button" className="flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 px-6 py-3 text-xs font-bold uppercase tracking-widest text-stone-950 gold-glow disabled:opacity-60">
          <Save size={14} /> {saving ? "Saving…" : "Save All Changes"}
        </button>
      </div>

      <div className="mt-6 rounded-2xl border border-amber-500/20 bg-[#0B1D14]/90 p-5">
        <label className={labelCls}>Phone number shown on the site</label>
        <input value={settings.phone_display} onChange={(e) => setSettings((s) => ({ ...s, phone_display: e.target.value }))} data-testid="settings-phone" className={inputCls} />
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-3">
        {settings.packages.map((p) => (
          <div key={p.id} className="rounded-2xl border border-amber-500/20 bg-[#0B1D14]/90 p-5" data-testid={`settings-package-${p.id}`}>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className={labelCls}>Package name</label>
                <input value={p.name} onChange={(e) => setPkg(p.id, "name", e.target.value)} data-testid={`settings-name-${p.id}`} className={inputCls} />
              </div>
              <div className="w-28">
                <label className={labelCls}>Price $</label>
                <input type="number" min="1" step="1" value={p.price} onChange={(e) => setPkg(p.id, "price", e.target.value)} data-testid={`settings-price-${p.id}`} className={inputCls} />
              </div>
            </div>
            <p className="mt-1.5 text-right font-mono-x text-[10px] uppercase tracking-widest text-amber-300/70">
              50% deposit: ${(parseFloat(p.price || 0) / 2).toFixed(2)}
            </p>
            <div className="mt-3">
              <label className={labelCls}>Badge</label>
              <input value={p.badge} onChange={(e) => setPkg(p.id, "badge", e.target.value)} className={inputCls} />
            </div>
            <div className="mt-3">
              <label className={labelCls}>Short subtitle</label>
              <input value={p.subtitle} onChange={(e) => setPkg(p.id, "subtitle", e.target.value)} className={inputCls} />
            </div>
            <div className="mt-3">
              <label className={labelCls}>Coverage line</label>
              <input value={p.coverage} onChange={(e) => setPkg(p.id, "coverage", e.target.value)} className={inputCls} />
            </div>
            <div className="mt-3">
              <label className={labelCls}>Best for</label>
              <input value={p.bestFor} onChange={(e) => setPkg(p.id, "bestFor", e.target.value)} className={inputCls} />
            </div>
            <div className="mt-3">
              <label className={labelCls}>What's included (one per line)</label>
              <textarea
                rows={9}
                value={p.included.join("\n")}
                onChange={(e) => setPkg(p.id, "included", e.target.value.split("\n").filter((l) => l.trim()))}
                data-testid={`settings-included-${p.id}`}
                className={`${inputCls} resize-y`}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-amber-500/20 bg-[#0B1D14]/90 p-5">
        <h3 className="font-display text-lg font-bold text-amber-50">Storage & Maintenance Add-on</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_140px]">
          <div>
            <label className={labelCls}>Add-on name</label>
            <input value={settings.addon.name} onChange={(e) => setAddon("name", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Price $</label>
            <input type="number" min="0" step="1" value={settings.addon.price} onChange={(e) => setAddon("price", parseFloat(e.target.value) || 0)} data-testid="settings-addon-price" className={inputCls} />
          </div>
        </div>
        <div className="mt-3">
          <label className={labelCls}>Description</label>
          <textarea rows={2} value={settings.addon.description} onChange={(e) => setAddon("description", e.target.value)} className={`${inputCls} resize-y`} />
        </div>
      </div>
    </div>
  );
};
